<template>
    <MultiplePopup
        v-model:stepIndex="currentStepIndex"
        v-bind:steps="tutorialSteps"
        v-bind:zIndex="ZIndex.Tutorial"
        v-bind:showCloseButton="showCloseButton"
        v-bind:isCentered="!isShowBetSlipMock"
        gaClassPrefix="SportsLobby_Tutorial_BetSlips"
        v-on:callback="emit('close')"
    >
        <template #backdrop>
            <SportLobbySkeleton v-on:update:spotlightCells="spotlightCells = $event" />
        </template>

        <template #content>
            <BetSlipMock v-if="isShowBetSlipMock" />
        </template>

        <template #overlay>
            <TutorialSpotlight
                v-if="currentSpotlight?.element"
                v-bind:target="currentSpotlight.element"
                v-bind:label="currentSpotlight.label"
            />
        </template>
    </MultiplePopup>
</template>

<script lang="ts" setup>
    import { computed, ref } from 'vue';
    import BetSlipMock from '@/components/betSlipTutorial/BetSlipMock.vue';
    import SportLobbySkeleton from '@/components/betSlipTutorial/SportLobbySkeleton.vue';
    import TutorialSpotlight from '@/components/betSlipTutorial/TutorialSpotlight.vue';
    import MultiplePopup from '@/components/common/MultiplePopup.vue';
    import { SingleModeBetSlipTutorialSpotlight, SingleModeBetSlipTutorialStep, ZIndex } from '@/interface/enum';
    import { useSpotlightRotation } from '@/libraries/composables/useSpotlightRotation';
    import { i18n } from '@/libraries/i18n';

    withDefaults(defineProps<{
        showCloseButton?: boolean;
    }>(), {
        showCloseButton: false,
    });

    const emit = defineEmits<{
        close: [];
    }>();

    const SPOTLIGHT_ODDS_LABELS: Record<SingleModeBetSlipTutorialSpotlight, string> = {
        [SingleModeBetSlipTutorialSpotlight.FirstLeagueOddsMock]: '8.80',
        [SingleModeBetSlipTutorialSpotlight.OtherLeagueOddsMock]: '0.97',
    };
    const TUTORIAL_STEPS = [
        {
            step: SingleModeBetSlipTutorialStep.ShowBetSlip,
            titleKey: 'parlay_in_single_tutorial_title_1',
            descriptionKey: 'parlay_in_single_tutorial_content_1',
        },
        {
            step: SingleModeBetSlipTutorialStep.HighlightLobbyOdds,
            titleKey: 'parlay_in_single_tutorial_title_2',
            descriptionKey: 'parlay_in_single_tutorial_content_2',
        },
    ];

    const currentStepIndex = ref(0);
    const spotlightCells = ref<{ name: SingleModeBetSlipTutorialSpotlight, element: HTMLElement | null }[]>([]);

    function isCurrentStep(step: SingleModeBetSlipTutorialStep) {
        return TUTORIAL_STEPS[currentStepIndex.value].step === step;
    }

    const tutorialSteps = computed(() => TUTORIAL_STEPS.map(({ titleKey, descriptionKey }) => ({
        title: i18n.t(titleKey),
        description: i18n.t(descriptionKey),
    })));
    const isShowBetSlipMock = computed(() => isCurrentStep(SingleModeBetSlipTutorialStep.ShowBetSlip));
    const isSpotlightStep = computed(() => isCurrentStep(SingleModeBetSlipTutorialStep.HighlightLobbyOdds));
    const spotlightTargets = computed(() => spotlightCells.value.map(({ name, element }) => ({
        element,
        label: SPOTLIGHT_ODDS_LABELS[name],
    })));

    const { currentTarget: currentSpotlight } = useSpotlightRotation(spotlightTargets, isSpotlightStep);
</script>

<style lang="scss" scoped>
@import "@/scss/components/betSlipTutorial/tutorial";
</style>
